import { useState } from "react";
import { BookOpen, Lock, Plus, Pin, Users2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNotebooks, useNotes, useCreateNotebook, useCreateNote, useUpdateNote, type Notebook, type Note } from "@/hooks/useNotebooks";

export default function Notes() {
  const { data: notebooks = [], isLoading } = useNotebooks();
  const [selectedNotebookId, setSelectedNotebookId] = useState<string | null>(null);
  const [showNewNotebook, setShowNewNotebook] = useState(false);
  const [newNbTitle, setNewNbTitle] = useState("");
  const [newNbShared, setNewNbShared] = useState(false);
  const createNotebook = useCreateNotebook();

  const selectedNotebook = notebooks.find((nb) => nb.id === selectedNotebookId) ?? notebooks[0];

  const handleCreateNotebook = () => {
    if (!newNbTitle.trim()) return;
    createNotebook.mutate(
      { title: newNbTitle, isShared: newNbShared },
      { onSuccess: (nb) => { setSelectedNotebookId(nb.id); setShowNewNotebook(false); setNewNbTitle(""); } },
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Notes</h1>
          <p className="text-sm text-muted-foreground">
            Personal and shared notebooks — your digital scratch pad.
          </p>
        </div>
        <button
          onClick={() => setShowNewNotebook(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> New Notebook
        </button>
      </div>

      {showNewNotebook && (
        <div className="rounded-xl border border-primary/30 bg-card p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <input
              type="text"
              value={newNbTitle}
              onChange={(e) => setNewNbTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateNotebook()}
              placeholder="Notebook title..."
              autoFocus
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={newNbShared}
                onChange={(e) => setNewNbShared(e.target.checked)}
                className="h-4 w-4 rounded border-border"
              />
              Shared with team
            </label>
            <button
              onClick={handleCreateNotebook}
              disabled={!newNbTitle.trim()}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40"
            >
              Create
            </button>
            <button onClick={() => setShowNewNotebook(false)} className="text-sm text-muted-foreground">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[280px_1fr]">
        {/* Notebook list */}
        <div className="space-y-2">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : notebooks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No notebooks yet. Create one to get started.</p>
          ) : (
            notebooks.map((nb) => (
              <button
                key={nb.id}
                onClick={() => setSelectedNotebookId(nb.id)}
                className={cn(
                  "flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors",
                  selectedNotebook?.id === nb.id
                    ? "border-primary/30 bg-primary/5"
                    : "border-border bg-card hover:border-primary/20",
                )}
              >
                {nb.isShared ? (
                  <Users2 className="h-4 w-4 text-blue-500" />
                ) : (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{nb.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {nb.isShared ? "Shared" : "Personal"}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Notes content */}
        {selectedNotebook ? (
          <NotesPanel notebook={selectedNotebook} />
        ) : (
          <div className="flex items-center justify-center rounded-xl border border-dashed border-border py-16">
            <div className="text-center">
              <BookOpen className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Select or create a notebook</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function NotesPanel({ notebook }: { notebook: Notebook }) {
  const { data: notes = [], isLoading } = useNotes(notebook.id);
  const createNote = useCreateNote();
  const updateNote = useUpdateNote();
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newContent, setNewContent] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  const handleCreate = () => {
    if (!newTitle.trim()) return;
    createNote.mutate(
      { notebookId: notebook.id, title: newTitle, contentHtml: newContent || undefined },
      { onSuccess: () => { setNewTitle(""); setNewContent(""); setShowNew(false); } },
    );
  };

  const handleSaveEdit = (note: Note) => {
    updateNote.mutate({
      notebookId: notebook.id,
      noteId: note.id,
      contentHtml: editContent,
    });
    setEditingId(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{notebook.title}</h2>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:bg-muted"
        >
          <Plus className="h-3.5 w-3.5" /> Add Note
        </button>
      </div>

      {showNew && (
        <div className="rounded-xl border border-primary/30 bg-card p-4 space-y-3">
          <input
            type="text"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder="Note title..."
            autoFocus
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <textarea
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
            placeholder="Write something..."
            rows={4}
            className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
          />
          <div className="flex gap-2">
            <button onClick={handleCreate} disabled={!newTitle.trim()} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-40">
              Save
            </button>
            <button onClick={() => setShowNew(false)} className="text-sm text-muted-foreground">Cancel</button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading notes...</p>
      ) : notes.length === 0 ? (
        <div className="flex flex-col items-center py-12 text-muted-foreground">
          <BookOpen className="h-8 w-8" />
          <p className="mt-2 text-sm">No notes yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map((note) => (
            <div
              key={note.id}
              className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/20"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {note.isPinned && <Pin className="h-3.5 w-3.5 text-amber-500" />}
                  <h3 className="text-sm font-semibold">{note.title}</h3>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {new Date(note.createdAt).toLocaleDateString()}
                </span>
              </div>
              {editingId === note.id ? (
                <div className="mt-2 space-y-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button onClick={() => handleSaveEdit(note)} className="rounded bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">Save</button>
                    <button onClick={() => setEditingId(null)} className="text-xs text-muted-foreground">Cancel</button>
                  </div>
                </div>
              ) : (
                <div
                  onClick={() => { setEditingId(note.id); setEditContent(note.contentHtml ?? ""); }}
                  className="mt-2 cursor-pointer text-sm text-muted-foreground line-clamp-3"
                >
                  {note.contentHtml || <span className="italic">Click to add content...</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
